/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.settings

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import studio.oddpods.cauce.core.base.ui.EventsEffect
import studio.oddpods.cauce.core.model.reminder.ReminderTemplate
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.asString
import studio.oddpods.cauce.core.ui.scaffold.KptScaffold
import studio.oddpods.cauce.feature.settings.generated.resources.Res
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_reminders_insert_variable
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_reminders_max_label
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_reminders_save
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_reminders_saved
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_reminders_template_hint
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_reminders_template_label
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_reminders_title

@Composable
internal fun ReminderSettingsScreen(
    onBackClick: () -> Unit,
    viewModel: ReminderSettingsViewModel = koinViewModel(),
) {
    val state by viewModel.stateFlow.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val savedMsg = stringResource(Res.string.feature_settings_reminders_saved)

    EventsEffect(eventFlow = viewModel.eventFlow) { event ->
        when (event) {
            ReminderSettingsEvent.Saved -> snackbarHostState.showSnackbar(savedMsg)
        }
    }

    KptScaffold(
        onNavigationIconClick = onBackClick,
        title = stringResource(Res.string.feature_settings_reminders_title),
        snackbarHost = {
            SnackbarHost(hostState = snackbarHostState)
        },
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(16.dp)
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            MaxField(
                max = state.maxReminders,
                error = state.maxError,
                onChange = { viewModel.trySendAction(ReminderSettingsAction.MaxRemindersChanged(it)) },
            )

            TemplateEditor(
                template = state.template,
                onTemplateChange = { viewModel.trySendAction(ReminderSettingsAction.TemplateChanged(it)) },
            )

            Button(
                onClick = { viewModel.trySendAction(ReminderSettingsAction.SaveClicked) },
                enabled = !state.isSaving,
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(stringResource(Res.string.feature_settings_reminders_save))
            }

            state.saveError?.let { error ->
                Text(
                    text = error.asString(),
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodySmall,
                )
            }
        }
    }
}

@Composable
private fun MaxField(
    max: Int,
    error: UiText?,
    onChange: (Int) -> Unit,
) {
    OutlinedTextField(
        value = max.toString(),
        onValueChange = { raw ->
            val n = raw.toIntOrNull() ?: 0
            onChange(n)
        },
        label = { Text(stringResource(Res.string.feature_settings_reminders_max_label)) },
        supportingText = error?.let { { Text(it.asString()) } },
        isError = error != null,
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
private fun TemplateEditor(
    template: String,
    onTemplateChange: (String) -> Unit,
) {
    var menuExpanded by remember { mutableStateOf(false) }
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        OutlinedTextField(
            value = template,
            onValueChange = onTemplateChange,
            label = { Text(stringResource(Res.string.feature_settings_reminders_template_label)) },
            placeholder = { Text(stringResource(Res.string.feature_settings_reminders_template_hint)) },
            modifier = Modifier.fillMaxWidth(),
            minLines = 4,
        )
        Box {
            TextButton(onClick = { menuExpanded = true }) {
                Text(stringResource(Res.string.feature_settings_reminders_insert_variable))
            }
            DropdownMenu(
                expanded = menuExpanded,
                onDismissRequest = { menuExpanded = false },
            ) {
                ReminderTemplate.ALL_PLACEHOLDERS.forEach { placeholder ->
                    DropdownMenuItem(
                        text = { Text(placeholder) },
                        onClick = {
                            onTemplateChange(template + placeholder)
                            menuExpanded = false
                        },
                    )
                }
            }
        }
    }
}
